import { WellConfigurationData, WellConfigurationIndex, GisContextLocationType } from "../../form-controls/components/new-casing-plan/models/casing-plan.model";

export interface GISConfig {
    tokenRenewalTimeframe: number;
    usdwAreasUrl: string;
    minesUrl: string;
    transportationUrl: string;
    waterWellsUrl: string;
    oilGasWellsUrl: string;
    serverEndPoint: string;
    clintonPinchoutGISLayer: string;
    fiveYearTravelDataUrl: string;
    restServicesUrl: string;
    yearlyGISDataUrl: string;
    biWeeklyGISDataUrl: string;
    dailyGISDataUrl: string;
    glacialDriftGISUrl: string;
    countiesGISLayer: string;
    statesGISLayer: string;
    townshipGISLayer: string;
    populationGISLayer: string;
    existingWellsGISLayer: string;
    gisPortalEndpoint: string;
    generateTokenEndpoint: string;
    tokenUsername: string;
    tokenPassword: string;
    tokenReferrer: string;
    routingClientId: string;
    routingClientSecret: string;
    routingServicesUrl: string;
    routingServicesApiKey: string;
    routingServicesProxy: string;
    routingServiesTokenEndpoint: string;
    elevationServicesProxy: string;
    elevationServicesUrl: string;
    watershedServicesUrl: string;
    wellViewerLandBase: string;
    wellViewerPublicWells: string;
    wellViewerCEA: string;
    wellViewerEarthQuakes: string;
    wellViewerNotificationAreas: string;
    wellStringsUrl: string;
    inlandSurfaceWaterSWPAUrl: string
}

export enum GISLayer {
    Mines = 0,
    Transportation = 1,
    WaterWells = 2,
    OilGasWells = 3
}

export interface GISOptions {
    alternateReadContextOptions: WellConfigurationData[];
    readContextOptions: WellConfigurationData[];
    writeContextOptions: WellConfigurationData[];
}

export function ensureOptionsExist(existing: GISOptions): GISOptions {
    const defaultOptions = getDefaultGISControllerOptions();
    if (!existing) return defaultOptions;
    const optionList = { alternateReadContextOptions: [], readContextOptions: [], writeContextOptions: [] } as GISOptions;
    if (existing.readContextOptions) {
        for (let o = 0; o < defaultOptions.readContextOptions.length; o++) {
            const opt = defaultOptions.readContextOptions[o];
            optionList.readContextOptions.push(getOption(opt, existing.readContextOptions));
        }
    } else {
        optionList.readContextOptions = defaultOptions.readContextOptions;
    }
    if (existing.writeContextOptions) {
        for (let o = 0; o < defaultOptions.writeContextOptions.length; o++) {
            const opt = defaultOptions.writeContextOptions[o];
            const option = getOption(opt, existing.writeContextOptions);
            optionList.writeContextOptions.push(option);
        }
    } else {
        optionList.writeContextOptions = defaultOptions.writeContextOptions;
    }    
    if (existing.alternateReadContextOptions) {
        for (let o = 0; o < defaultOptions.alternateReadContextOptions.length; o++) {
            const opt = defaultOptions.alternateReadContextOptions[o];
            const option = getOption(opt, existing.alternateReadContextOptions);
            optionList.alternateReadContextOptions.push(option);
        }
    } else {
        optionList.alternateReadContextOptions = defaultOptions.alternateReadContextOptions;
    }
    return optionList;
}

export function getDefaultGISControllerOptions(): GISOptions {
    return {
        alternateReadContextOptions: [
            {
                index: WellConfigurationIndex.LatitudeNumeric,
                displayName: "Latitude (Numeric)",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LongitudeNumeric,
                displayName: "Longitude (Numeric)",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData
        ],
        readContextOptions: [
            {
                index: WellConfigurationIndex.WellDirection,
                displayName: "Well Direction",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LatitudeDirectional,
                displayName: "NAD83 Y - Directional Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LatitudeHorizontal,
                displayName: "NAD83 Y - Horizontal Well", 
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LatitudeVertical,
                displayName: "NAD83 Y - Vertical Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LongitudeDirectional,
                displayName: "NAD83 X - Directional Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LongitudeHorizontal,
                displayName: "NAD83 X - Horizontal Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LongitudeVertical,
                displayName: "NAD83 X - Vertical Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.StatePlaneVertical,
                displayName: "State Plane - Vertical Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.StatePlaneHorizontal,
                displayName: "State Plane - Horizontal Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.StatePlaneDirectional,
                displayName: "State Plane - Directional Well",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.FirstTakeNADX,
                displayName: "Well First Take NAD83 X",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.FirstTakeNADY,
                displayName: "Well First Take NAD83 Y",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.FirstTakeStatePlane,
                displayName: "Well First Take State Plane",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LastTakeNADX,
                displayName: "Well Last Take NAD83 X",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LastTakeNADY,
                displayName: "Well Last Take NAD83 Y",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.LastTakeStatePlane,
                displayName: "Well Last Take State Plane",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.TargetNADX,
                displayName: "Well Target Take NAD83 X",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.TargetNADY,
                displayName: "Well Target Take NAD83 Y",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.TargetStatePlane,
                displayName: "Well Target Take State Plane",
                location: "",
                locationType: GisContextLocationType.ReadOnly
            } as WellConfigurationData
        ] as WellConfigurationData[],
        writeContextOptions: [
            {
                index: WellConfigurationIndex.State,
                displayName: "State",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.SectionLotTract,
                displayName: "Section / Lot / Tract",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.Section,
                displayName: "Section",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.Lot,
                displayName: "Lot",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.Tract,
                displayName: "Tract",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.QuadrangleName,
                displayName: "Quadrangle Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.QuadrangleCode,
                displayName: "Quadrangle Code",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.CountyName,
                displayName: "County Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.CountyCode,
                displayName: "County Code",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.TownshipName,
                displayName: "Township Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.TownshipNumber,
                displayName: "Township Number",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            
            {
                index: WellConfigurationIndex.CalculatedLatitude,
                displayName: "Latitude (calculated)",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            },
            {
                index: WellConfigurationIndex.CalculatedLongitude,
                displayName: "Longitude (calculated)",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            },
            {
                index: WellConfigurationIndex.UrbanArea,
                displayName: "Urban Area",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.WaterSourceLatitude,
                displayName: "Water Source - Latitude",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.WaterSourceLongitude,
                displayName: "Water Source - Longitude",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.Watershed,
                displayName: "Watershed",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.WellheadToWellheadVariance,
                displayName: "Wellhead to Wellhead Variance",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionBodiesOfWater,
                displayName: "Proximity to Body of Water",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionDriftThickness,
                displayName: "Drift Thickness",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionHundredYearFloodPlain,
                displayName: "100 Year Flood Plain",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionHydrogenSulfide,
                displayName: "Hydrogen Sulfide",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionInjectionWellAOR,
                displayName: "Injection Well Area of Review",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionStateLands,
                displayName: "State Lands",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            },
            {
                index: WellConfigurationIndex.ConditionLakePlain,
                displayName: "Lake Plain Area",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionMeccaPool,
                displayName: "Mecca Pool",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData, {
                index: WellConfigurationIndex.ConditionAbandonedMines,
                displayName: "Abandoned Mines",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            }, {
                index: WellConfigurationIndex.ConditionActiveMines,
                displayName: "Active Mines",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionMunicipalWater,
                displayName: "Municipal Water",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionRoadsRailroads,
                displayName: "Roads and Railroads",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionSeismicArea,
                displayName: "Seismic Area",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionShallowSurface,
                displayName: "Shallow Surface",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionThinTill,
                displayName: "Thin Till",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionUndergroundDrinkingWater,
                displayName: "Underground Drinking Water (USDW)",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionUrbanArea,
                displayName: "Urban Area Condition",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionWetlands,
                displayName: "Wetlands",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.InspectorName,
                displayName: "Inspector Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.InspectorPhone,
                displayName: "Inspector Phone",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.BackupInspectorName,
                displayName: "Backup Inspector Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.BackupInspectorPhone,
                displayName: "Backup Inspector Phone",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.RegionalPhone,
                displayName: "Regional Phone",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.SupervisorName,
                displayName: "Supervisor Name",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.SupervisorPhone,
                displayName: "Supervisor Phone",
                location: "",
                locationType: GisContextLocationType.LocationWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionGasStorage,
                displayName: "Gas Storage",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionCoalBearing,
                displayName: "Coal Bearing Township",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionSprings,
                displayName: "Proximity to Springs",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionPerennialStreams,
                displayName: "Proximity to Perennial Streams",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData,
            {
                index: WellConfigurationIndex.ConditionWaterWellProximity,
                displayName: "Proximity to Water Wells",
                location: "",
                locationType: GisContextLocationType.ConditionWrite
            } as WellConfigurationData
        ] as WellConfigurationData[]
    } as GISOptions;
}

export function getOption(option: WellConfigurationData, list: WellConfigurationData[]): WellConfigurationData {
    const index = list.findIndex(fi => fi.index === option.index);
    if (index === -1) return option;
    list[index].displayName = option.displayName
    list[index].locationType = option.locationType;
    return list[index];
}